import { useStats } from '../../hooks/useStats';
import { useFilterState } from '../../hooks/useFilterState';
import MajorityProgress from '../shared/MajorityProgress';

export default function StatsStrip({ onStatClick }) {
  const { data: stats, isLoading } = useStats();
  const { updateFilters } = useFilterState();

  if (isLoading) {
    return (
      <div className="border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
          <div className="h-8 rounded-full animate-shimmer" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-16 sm:h-20 rounded-xl animate-shimmer" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const handleCompetitiveClick = () => {
    updateFilters({
      competitivenessRange: [70, 100],
      marginRange: [-10, 10],
      activePreset: 'top-50'
    });
    onStatClick?.();
  };

  const handleOpenSeatsClick = () => {
    updateFilters({
      openSeatsOnly: true,
      competitivenessRange: [40, 100],
      activePreset: 'open-opportunities'
    });
    onStatClick?.();
  };

  return (
    <div className="border-b border-slate-700 bg-gradient-to-b from-slate-800/50 to-transparent">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
        {/* Majority Progress Bar */}
        <MajorityProgress
          demSeats={stats?.demSeats || 213}
          repSeats={stats?.repSeats || 222}
          competitive={stats?.competitive || 0}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <StatCard
            label="Total Seats"
            value={stats?.total || 435}
            subtext="US House"
            variant="neutral"
            icon="🏛️"
          />
          <StatCard
            label="For Majority"
            value={stats?.majority || 218}
            subtext="Needed to win"
            variant="neutral"
            icon="🎯"
          />
          <StatCard
            label="Competitive"
            value={stats?.competitive || 0}
            subtext="In play"
            variant="purple"
            onClick={handleCompetitiveClick}
            clickable
            icon="⚔️"
          />
          <StatCard
            label="Open Seats"
            value={stats?.openSeats || 0}
            subtext="No incumbent"
            variant="orange"
            onClick={handleOpenSeatsClick}
            clickable
            icon="🆕"
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, subtext, variant, onClick, clickable, icon }) {
  const Component = clickable ? 'button' : 'div';

  const variantStyles = {
    neutral: 'bg-slate-800/60 border-slate-700 hover:border-slate-500',
    purple: 'bg-gradient-to-br from-purple-900/40 to-purple-700/20 border-purple-500/30 hover:border-purple-400/60',
    orange: 'bg-gradient-to-br from-orange-900/40 to-orange-700/20 border-orange-500/30 hover:border-orange-400/60',
    blue: 'bg-gradient-to-br from-blue-900/40 to-blue-700/20 border-blue-500/30 hover:border-blue-400/60',
  };

  const valueColors = {
    neutral: 'text-white',
    purple: 'text-purple-300',
    orange: 'text-orange-300',
    blue: 'text-blue-300',
  };

  return (
    <Component
      onClick={onClick}
      className={`
        rounded-xl p-2.5 sm:p-3 transition-all text-left w-full border backdrop-blur-sm min-h-[72px] sm:min-h-[88px]
        ${variantStyles[variant] || variantStyles.neutral}
        ${clickable
          ? 'cursor-pointer hover:scale-[1.03] hover:shadow-xl active:scale-[0.98]'
          : ''
        }
      `}
    >
      <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
        {icon && <span className="text-sm sm:text-base">{icon}</span>}
        <span className="text-[10px] sm:text-xs text-slate-400 font-medium uppercase tracking-wide truncate">{label}</span>
      </div>
      <div className={`text-xl sm:text-3xl font-bold ${valueColors[variant] || valueColors.neutral}`}>
        {value}
      </div>
      <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 flex items-center gap-1">
        <span className="truncate">{subtext}</span>
        {clickable && <span className="text-current opacity-60 flex-shrink-0">→</span>}
      </div>
    </Component>
  );
}
